import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Grid,
  Typography,
  FormHelperText,
  CircularProgress,
} from "@mui/material";

import { getDocumentFile } from "../../../services/DocumentService";
import { convertDate } from "../../../utils/function";

import FileUploadCard from "../../FileUploadCard";

const DocumentDetail = ({ data, handleSave }) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const retreiveDocumentFile = useCallback(async (docId) => {
    try {
      setLoading(true);
      const res = await getDocumentFile(docId);
      setLoading(false);

      if (res.length) setSelectedFile(res[0]);
    } catch (e) {
      console.error(e);
      toast.error("Unexpected error occurs while loading the document!");
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!data) return;

    retreiveDocumentFile(data.DOCUMENT_ID).then(() => {
      if (!isMounted) return;
    });

    return () => {
      isMounted = false;
    };
  }, [data, retreiveDocumentFile]);

  const handleRowEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsEditing(true);
    setError(Boolean(!selectedFile));
  };
  const handleRowEditSave = (e) => {
    setError(Boolean(!selectedFile));

    if (!selectedFile) return;

    handleSave(selectedFile);
  };
  const handleRowEditCancel = (e) => {
    setError(false);
    setIsEditing(false);
  };

  const handleFileChange = (fileData) => {
    if (!fileData) {
      return;
    }

    setError(Boolean(!fileData));
    setSelectedFile({
      ...selectedFile,
      name: fileData.name,
      FILE_STREAM: fileData.file,
    });
  };

  const handleDocumentDownload = () => {
    const { name: fileName, FILE_STREAM: fileStream } = selectedFile;

    if (!fileStream) return;

    const binaryData = atob(fileStream);
    const byteArray = new Uint8Array(binaryData.length);

    for (let i = 0; i < binaryData.length; i++) {
      byteArray[i] = binaryData.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: "application/octet-stream" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  };

  return (
    <Grid container>
      <Grid item xs={6}>
        <Grid container>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="span" component="p" align="right">
                  <strong>Supplier:</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="span">
                  {data?.SUPPLIER_USERNAME || ""}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="span" component="p" align="right">
                  <strong>Created at:</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="span">
                  {data?.CREATE_DATE ? convertDate(data?.CREATE_DATE) : ""}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="span" component="p" align="right">
                  <strong>Updated at:</strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="span">
                  {data?.LAST_UPDATED ? convertDate(data?.LAST_UPDATED) : ""}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {!isEditing && (
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="span" component="p" align="right">
                    <strong>Document:</strong>
                  </Typography>
                </Grid>
                {loading ? (
                  <Grid item>
                    <CircularProgress size="1rem" />
                  </Grid>
                ) : (
                  selectedFile && (
                    <Grid item xs={6}>
                      <Button variant="text" onClick={handleDocumentDownload}>
                        {selectedFile.FILE_STREAM
                          ? selectedFile.name
                          : "Invalid file!"}
                      </Button>
                    </Grid>
                  )
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Grid container>
          <Grid item xs={6}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                {isEditing && (
                  <>
                    <FileUploadCard
                      file={
                        selectedFile
                          ? {
                              name: selectedFile.name || "",
                              size: atob(selectedFile.FILE_STREAM).length,
                              file: selectedFile.FILE_STREAM,
                            }
                          : null
                      }
                      handleChange={handleFileChange}
                    />
                    {error && (
                      <FormHelperText error>
                        Attached document is required.
                      </FormHelperText>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid container justifyContent="flex-end">
              <Grid item>
                {isEditing ? (
                  <Grid container spacing={1}>
                    <Grid item>
                      <Button variant="outlined" onClick={handleRowEditCancel}>
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button variant="contained" onClick={handleRowEditSave}>
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <Button variant="outlined" onClick={handleRowEdit}>
                    Edit
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DocumentDetail;
